<template>
  <div class="page thought">
    <p v-if="showLowRateWarn(kpis.participateRate)" class="warn-tip">
      本月学习参与率偏低，请及时通知会员学习
    </p>

    <div class="kpi-row">
      <div class="kpi">
        <div class="kpi__left">
          <span class="kpi__icon-wrap"><i class="ri-book-open-fill kpi__icon" aria-hidden="true" /></span>
          <span class="kpi__label">年度学习次数</span>
        </div>
        <b>{{ kpiInt("sessions") }}<em>次</em></b>
      </div>
      <div class="kpi kpi--rate" @click="openRateDetail">
        <div class="kpi__left">
          <span class="kpi__icon-wrap"><i class="ri-pie-chart-fill kpi__icon" aria-hidden="true" /></span>
          <span class="kpi__label">学习参与率</span>
        </div>
        <div class="rate-ring" :style="rateRingStyle">
          <strong>{{ kpiInt("participateRate") }}%</strong>
        </div>
      </div>
      <div class="kpi" @click="openNotesList">
        <div class="kpi__left">
          <span class="kpi__icon-wrap"><i class="ri-quill-pen-fill kpi__icon" aria-hidden="true" /></span>
          <span class="kpi__label">学习心得数</span>
        </div>
        <b>{{ kpiInt("notes") }}<em>篇</em></b>
      </div>
      <div class="kpi" @click="openBrandParticipants">
        <div class="kpi__left">
          <span class="kpi__icon-wrap"><i class="ri-award-fill kpi__icon" aria-hidden="true" /></span>
          <span class="kpi__label">品牌活动参与人数</span>
        </div>
        <b>{{ kpiInt("brandParticipants") }}<em>人次</em></b>
      </div>
    </div>

    <div class="cols">
      <div class="col col--left">
        <section class="panel">
          <h3>官方品牌活动</h3>
          <div class="brands">
            <button
              v-for="b in brands"
              :key="b.key"
              type="button"
              class="brand-card"
              :class="'theme-' + b.theme"
              @click="openBrand(b)"
            >
              <div class="brand-card__head">
                <strong>{{ b.name }}</strong>
                <em :class="{ done: b.status === '已完成' }">{{ b.status || "进行中" }}</em>
              </div>
              <div class="brand-card__metrics">
                <span v-for="(m, i) in b.metrics || []" :key="i">
                  {{ m.label }} <b>{{ m.value }}</b>
                </span>
              </div>
            </button>
          </div>
        </section>

        <section class="panel panel--chart">
          <h3>学习完成率趋势</h3>
          <div ref="trendChart" class="chart"></div>
          <p class="avg">平均参与率 {{ payload.avgRate || 0 }}%</p>
        </section>
      </div>

      <div class="col col--right">
        <section class="panel panel--notes">
          <h3>优秀学习心得墙</h3>
          <div class="notes-wall">
            <button
              v-for="(n, i) in notes"
              :key="i"
              type="button"
              class="note-card"
              @click="openNote(n)"
            >
              <div class="note-card__avatar" :style="avatarStyle(n)">
                <span v-if="!avatarUrlOf(n)">{{ nameInitial(n) }}</span>
              </div>
              <div class="note-card__body">
                <strong>{{ n.author || "会员" }}</strong>
                <em>{{ n.unit || "" }}</em>
                <p>{{ n.summary || n.title }}</p>
              </div>
            </button>
            <p v-if="!notes.length" class="empty">暂无优秀心得</p>
          </div>
        </section>

        <section class="panel panel--photos">
          <h3>学习活动照片墙</h3>
          <div class="photo-wall">
            <button
              v-for="(p, i) in photos"
              :key="i"
              type="button"
              class="photo-card"
              @click="openLightbox(photos, i)"
            >
              <div
                class="photo-card__img"
                :style="p.coverUrl ? { backgroundImage: 'url(' + absUrl(p.coverUrl) + ')' } : {}"
              />
              <span class="photo-card__date">{{ p.time }}</span>
              <span class="photo-card__title">{{ p.title }}</span>
            </button>
            <p v-if="!photos.length" class="empty">暂无照片，请在后台上传学习活动图片</p>
          </div>
        </section>
      </div>
    </div>

    <div v-if="lightbox.open" class="lb" @click.self="closeLightbox">
      <button type="button" class="lb__nav lb__prev" @click="lightboxPrev">‹</button>
      <div class="lb__body">
        <img v-if="lbItem.coverUrl" :src="absUrl(lbItem.coverUrl)" :alt="lbItem.title" />
        <div v-else class="lb__placeholder">{{ lbItem.title }}</div>
        <p>{{ lbItem.title }} · {{ lbItem.time }}</p>
      </div>
      <button type="button" class="lb__nav lb__next" @click="lightboxNext">›</button>
      <button type="button" class="lb__close" @click="closeLightbox">×</button>
    </div>
  </div>
</template>

<script>
import { LineChart } from "@/plugins/echarts_bank/index";
import branchQuery from "@/utils/branchQuery";
import { absMediaUrl, toDetailItem, profileAvatarUrl, memberAvatarStyle, memberNameInitial } from "@/utils/mediaUrl";
import { fetchBrandMaterialDrawer } from "@/utils/brandMaterials";
import { isSameJson } from "@/utils/stableData";
import dashPage from "@/mixins/dashPage";

export default {
  name: "DashThought",
  mixins: [branchQuery, dashPage],
  data() {
    return { payload: {}, chart: null, trendSig: null };
  },
  computed: {
    kpis() {
      return this.payload.kpis || {};
    },
    brands() {
      return this.payload.brands || [];
    },
    notes() {
      return (this.payload.notes || []).slice(0, 12);
    },
    photos() {
      return this.payload.photos || [];
    },
    rateRingStyle() {
      const r = Number(this.kpis.participateRate) || 0;
      const c = this.rateColor(r, this.inspectMode);
      return {
        background: `conic-gradient(${c} 0 ${r}%, rgba(255,255,255,0.12) ${r}% 100%)`
      };
    },
    lbItem() {
      return this.lightbox.list[this.lightbox.index] || {};
    }
  },
  watch: {
    branchQuery: {
      deep: true,
      handler() {
        this.load();
      }
    },
    payload() {
      const trend = this.payload.trend || [];
      this.redrawIfChanged("trendSig", trend, () => {
        this.$nextTick(this.renderTrend);
      });
      const k = this.payload.kpis || {};
      this.syncRollKpis({
        sessions: k.sessions || 0,
        participateRate: k.participateRate || 0,
        notes: k.notes || 0,
        brandParticipants: k.brandParticipants || 0
      });
    }
  },
  mounted() {
    const fs = this.commonFont;
    this.chart = new LineChart({
      ele: this.$refs.trendChart,
      fontSize: fs,
      unit: "%",
      palletColors: [["#00c3ff", "#4c7dff"]],
      grid: {
        top: 2.4 * fs,
        right: 1.2 * fs,
        bottom: 2.2 * fs,
        left: 2.8 * fs
      },
      series: [
        {
          name: "参与率",
          smooth: true,
          showSymbol: true,
          symbolSize: 8,
          label: {
            show: true,
            position: "top",
            distance: 4,
            fontSize: Math.max(10, fs - 2),
            color: "#7dffc8",
            formatter(p) {
              const v = p.value;
              return v == null ? "" : `${v}%`;
            }
          },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(0,195,255,0.35)" },
                { offset: 1, color: "rgba(0,195,255,0.02)" }
              ]
            }
          }
        }
      ]
    });
    this.load();
    this.startPageRefresh();
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  },
  methods: {
    absUrl: absMediaUrl,
    avatarUrlOf(n) {
      return profileAvatarUrl(n);
    },
    avatarStyle(n) {
      return memberAvatarStyle(n, { profileOnly: true });
    },
    nameInitial(n) {
      return memberNameInitial(n);
    },
    async load() {
      const res = await this.$api("screen", "thought", this.branchQuery);
      if (res && res.code === 0) {
        if (isSameJson(this.payload, res)) return;
        this.payload = res;
      }
    },
    renderTrend() {
      if (!this.chart) return;
      const trend = this.payload.trend || [];
      const points = trend.length
        ? trend.map(t => ({
            name: t.name,
            value: Number(t.rate) || 0,
            people: t.people || 0,
            month: t.month
          }))
        : [{ name: "—", value: 0, people: 0 }];
      this.chart
        .updateOption(
          {
            tooltip: {
              trigger: "axis",
              formatter(res) {
                const item = (res && res[0]) || {};
                const idx = item.dataIndex;
                const row = points[idx] || {};
                const month = row.month || item.name || "";
                const rate = row.value != null ? row.value : item.value;
                const people = row.people != null ? row.people : 0;
                return `${month} 学习参与率 ${rate}%，参与 ${people} 人`;
              }
            }
          },
          false
        )
        .setData([points])
        .draw();
    },
    openRateDetail() {
      const trend = (this.payload.trend || []).slice().reverse();
      const items = trend.map(t => {
        const rate = Number(t.rate) || 0;
        let status = "无场次";
        if (rate >= 80) status = "达标";
        else if (rate >= 70) status = "关注";
        else if (rate > 0) status = "偏低";
        return toDetailItem({
          title: t.month || t.name || "—",
          category: `参与率 ${rate}%`,
          status,
          content: `参与 ${t.people || 0} 人 · 场次 ${t.sessions || 0} 场`
        });
      });
      this.openDrawer({
        title: "学习参与率明细",
        summary: [
          `当前参与率 ${this.kpis.participateRate || 0}%`,
          `近12月平均 ${this.payload.avgRate || 0}%`
        ],
        items
      });
    },
    openNotesList() {
      const items = this.notes.map(n =>
        toDetailItem({
          title: n.title,
          content: n.content || n.summary,
          author: n.author,
          time: n.time,
          coverUrl: n.coverUrl,
          avatarUrl: n.avatarUrl
        })
      );
      this.openDrawer({
        title: "优秀心得",
        items
      });
    },
    openNote(n) {
      const item = toDetailItem({
        title: n.title,
        content: n.content || n.summary,
        author: [n.author, n.unit].filter(Boolean).join(" · "),
        time: n.time,
        coverUrl: n.coverUrl,
        avatarUrl: n.avatarUrl
      });
      this.openDrawer({ title: "心得详情", items: [item] });
    },
    async openBrand(b) {
      if (!b) return;
      const payload = await fetchBrandMaterialDrawer(
        this.$api.bind(this),
        b,
        this.branchQuery
      );
      this.openDrawer(payload);
    },
    openBrandParticipants() {
      const items = (this.brands || []).map(b => {
        const metrics = (b.metrics || [])
          .map(m => `${m.label} ${m.value}`)
          .join(" · ");
        return toDetailItem({
          title: b.name,
          category: `参与 ${b.participants || 0} 人次`,
          status: b.status || "进行中",
          content: metrics || "暂无分项指标",
          coverUrl: b.coverUrl
        });
      });
      this.openDrawer({
        title: "品牌活动参与明细",
        summary: [`合计 ${this.kpis.brandParticipants || 0} 人次`],
        items
      });
    }
  }
};
</script>

<style lang="less" scoped>
.page {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10 / @rem2;
  min-height: 0;
}
.warn-tip {
  margin: 0;
  padding: 6 / @rem2 12 / @rem2;
  color: #ffb070;
  background: rgba(255, 138, 61, 0.12);
  border: 1 / @rem2 solid rgba(255, 138, 61, 0.35);
  border-radius: 4 / @rem2;
  font-size: 13 / @rem2;
}
.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10 / @rem2;
}
.kpi {
  min-height: 80 / @rem2;
  box-sizing: border-box;
  padding: 12 / @rem2 14 / @rem2;
  border: 1 / @rem2 solid fade(@mainColor, 35%);
  border-radius: 6 / @rem2;
  background: rgba(8, 28, 64, 0.55);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10 / @rem2;
  cursor: pointer;
  &__left {
    display: flex;
    align-items: center;
    gap: 10 / @rem2;
    min-width: 0;
  }
  &__icon-wrap {
    position: relative;
    width: 36 / @rem2;
    height: 36 / @rem2;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    &::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: radial-gradient(
        circle,
        fade(@mainColor, 32%) 0%,
        fade(@mainColor, 8%) 50%,
        transparent 72%
      );
    }
  }
  &__icon {
    position: relative;
    z-index: 1;
    font-size: 20 / @rem2;
    line-height: 1;
    color: #7ae7ff;
    filter: drop-shadow(0 0 4px fade(@mainColor, 65%));
  }
  &__label {
    color: @assistColor;
    font-size: 14 / @rem2;
    white-space: nowrap;
  }
  b {
    font-size: 26 / @rem2;
    color: #fff;
    flex-shrink: 0;
    em {
      font-style: normal;
      font-size: 12 / @rem2;
      margin-left: 4 / @rem2;
      color: fade(#fff, 55%);
    }
  }
}
.kpi--rate {
  .rate-ring {
    width: 56 / @rem2;
    height: 56 / @rem2;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    &::after {
      content: "";
      position: absolute;
      inset: 6 / @rem2;
      border-radius: 50%;
      background: rgba(6, 22, 52, 0.95);
    }
    strong {
      position: relative;
      z-index: 1;
      color: #fff;
      font-size: 14 / @rem2;
    }
  }
}
.cols {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10 / @rem2;
}
.col {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10 / @rem2;
}
.panel {
  padding: 12 / @rem2;
  border: 1 / @rem2 solid fade(@assistColor, 22%);
  border-radius: 6 / @rem2;
  background: rgba(6, 22, 52, 0.55);
  display: flex;
  flex-direction: column;
  min-height: 0;
  h3 {
    margin: 0 0 10 / @rem2;
    color: @mainColor;
    font-size: 16 / @rem2;
  }
}
.panel--chart {
  flex: 1;
}
.col--right {
  .panel--notes {
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
  }
  .panel--photos {
    flex: 0 0 auto;
    overflow: hidden;
  }
}
.chart {
  flex: 1;
  min-height: 160 / @rem2;
}
.avg {
  margin: 4 / @rem2 0 0;
  text-align: center;
  color: fade(#fff, 65%);
  font-size: 13 / @rem2;
}
.brands {
  display: flex;
  flex-direction: column;
  gap: 8 / @rem2;
}
.brand-card {
  text-align: left;
  border: 1 / @rem2 solid fade(#fff, 12%);
  border-radius: 6 / @rem2;
  padding: 10 / @rem2 12 / @rem2;
  cursor: pointer;
  color: #e8f3ff;
  background: rgba(0, 120, 200, 0.22);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  &.theme-green {
    background: rgba(24, 160, 110, 0.28);
  }
  &.theme-gold {
    background: rgba(180, 130, 40, 0.3);
  }
  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8 / @rem2;
    min-width: 0;
    strong {
      font-size: 15 / @rem2;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    em {
      flex-shrink: 0;
      font-style: normal;
      font-size: 12 / @rem2;
      padding: 2 / @rem2 8 / @rem2;
      border-radius: 3 / @rem2;
      background: rgba(255, 138, 61, 0.85);
      color: #fff;
      &.done {
        background: rgba(255, 255, 255, 0.25);
      }
    }
  }
  &__metrics {
    margin-top: 8 / @rem2;
    display: flex;
    flex-wrap: wrap;
    gap: 10 / @rem2;
    span {
      font-size: 12 / @rem2;
      color: fade(#fff, 70%);
      b {
        color: #fff;
        margin-left: 4 / @rem2;
      }
    }
  }
}
.notes-wall {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: repeat(6, minmax(0, 1fr));
  gap: 6 / @rem2;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  min-width: 0;
  align-content: stretch;
}
.note-card {
  display: flex;
  gap: 8 / @rem2;
  padding: 4 / @rem2 8 / @rem2;
  border-radius: 6 / @rem2;
  background: rgba(255, 255, 255, 0.08);
  border: 1 / @rem2 solid fade(#fff, 10%);
  text-align: left;
  color: #e8f3ff;
  cursor: pointer;
  width: 100%;
  height: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  align-items: center;
  &__avatar {
    width: 36 / @rem2;
    height: 36 / @rem2;
    border-radius: 50%;
    background: rgba(0, 195, 255, 0.25) center / cover no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    span {
      font-size: 14 / @rem2;
    }
  }
  &__body {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    strong {
      display: block;
      font-size: 13 / @rem2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    em {
      display: block;
      font-style: normal;
      font-size: 11 / @rem2;
      color: fade(#fff, 45%);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    p {
      margin: 4 / @rem2 0 0;
      font-size: 12 / @rem2;
      color: fade(#fff, 72%);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
.photo-wall {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 88 / @rem2;
  gap: 6 / @rem2;
  /* 固定 3 行：3*88 + 2*6，超出可滚动 */
  height: 276 / @rem2;
  flex: none;
  overflow-x: hidden;
  overflow-y: auto;
  align-content: start;
  &::-webkit-scrollbar {
    width: 4 / @rem2;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: fade(@assistColor, 35%);
    border-radius: 4 / @rem2;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: fade(@assistColor, 55%);
  }
  scrollbar-width: thin;
  scrollbar-color: fade(@assistColor, 35%) transparent;
}
.photo-card {
  position: relative;
  border: 0;
  padding: 0;
  border-radius: 4 / @rem2;
  overflow: hidden;
  cursor: pointer;
  background: rgba(0, 80, 140, 0.35);
  height: 88 / @rem2;
  &__img {
    width: 100%;
    height: 100%;
    background: rgba(0, 100, 160, 0.4) center / cover no-repeat;
  }
  &__date {
    position: absolute;
    left: 4 / @rem2;
    top: 4 / @rem2;
    font-size: 10 / @rem2;
    padding: 1 / @rem2 4 / @rem2;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    border-radius: 2 / @rem2;
  }
  &__title {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 4 / @rem2;
    font-size: 11 / @rem2;
    color: #fff;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.65));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.empty {
  color: fade(#fff, 45%);
  font-size: 13 / @rem2;
  grid-column: 1 / -1;
}
.lb {
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  &__body {
    max-width: 70vw;
    text-align: center;
    img {
      max-width: 70vw;
      max-height: 75vh;
      object-fit: contain;
    }
    p {
      color: #fff;
      margin-top: 10 / @rem2;
    }
  }
  &__placeholder {
    width: 50vw;
    height: 40vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(20, 60, 110, 0.8);
    color: #fff;
    font-size: 20 / @rem2;
  }
  &__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 48 / @rem2;
    height: 48 / @rem2;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    font-size: 28 / @rem2;
    cursor: pointer;
  }
  &__prev {
    left: 24 / @rem2;
  }
  &__next {
    right: 24 / @rem2;
  }
  &__close {
    position: absolute;
    top: 20 / @rem2;
    right: 28 / @rem2;
    border: 0;
    background: transparent;
    color: #fff;
    font-size: 32 / @rem2;
    cursor: pointer;
  }
}
</style>
